import React, { useEffect, useState } from "react";
import RoutesList from "./routes";
import { useDispatch } from "react-redux";

import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

import { setUserAccessPermission } from "./features/access-permissions";
import GlobalButton from "./components/core/global-button";
import GlobalDialog from "./components/core/global-dialog";

function App() {
  const dispatch = useDispatch();
  const version = localStorage.getItem("version") ?? 0;

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [logoutReason, setLogoutReason] = useState(""); // "version" | "session"
  const [isTabHidden, setIsTabHidden] = useState(false);

  // ─── Version check ────────────────────────────────────────────────────────
  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT !== "test") {
      const isOnLoginPage =
        window.location.pathname === "/operator/admin/" ||
        window.location.pathname === "/operator/admin";
      if (!isOnLoginPage && version !== process.env.REACT_APP_VERSION) {
        localStorage.setItem("version", process.env.REACT_APP_VERSION);
        setLogoutReason("version");
        setOpenLogoutDialog(true);
      }
    }
  }, [version]);

  // ─── Session expired (from Apollo errorLink) ──────────────────────────────
  useEffect(() => {
    const handleSessionExpired = () => {
      // Save the current URL so we can redirect back after re-login
      const currentPath = window.location.pathname + window.location.search;
      if (
        currentPath !== "/operator/admin" &&
        currentPath !== "/operator/admin/"
      ) {
        sessionStorage.setItem("returnUrl", currentPath);
      }
      setLogoutReason("session");
      setOpenLogoutDialog(true);
    };
    window.addEventListener("sessionExpired", handleSessionExpired);
    return () =>
      window.removeEventListener("sessionExpired", handleSessionExpired);
  }, []);

  // ─── Permissions ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (localStorage.getItem("permission")) {
      dispatch(
        setUserAccessPermission(JSON.parse(localStorage.getItem("permission"))),
      );
    }
  }, []);

  // ─── Tab title + screen blur ──────────────────────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      const user = localStorage.getItem("token");
      if (!user) return;
      setIsTabHidden(document.hidden);
      document.title = document.hidden
        ? "● Operator Admin Portal"
        : "Operator Admin Portal";
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // ─── Cross-tab sync ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Another tab logged out
      if (event.key === "logoutTriggered" && event.newValue) {
        localStorage.clear();
        window.location.replace("/operator/admin/");
      }
      // Another tab logged in — reload to pick up the session
      if (event.key === "user" && event.newValue) {
        window.location.reload();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // ─── Logout ───────────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.clear();
    window.location.replace("/operator/admin/");
  };

  return (
    <div>
      <RoutesList />

      {/* ── Screen blur when tab is hidden ── */}
      {isTabHidden && localStorage.getItem("user") && (
        <div
          className="fixed inset-0 z-[9999] backdrop-blur-xl bg-black/50 flex items-center justify-center cursor-pointer"
          onClick={() => setIsTabHidden(false)}
        >
          <div className="text-white text-center pointer-events-none">
            <i className="pi pi-lock text-5xl mb-4 block" />
            <p className="text-xl font-semibold">Screen Locked</p>
            <p className="text-sm text-gray-300 mt-1">
              Click anywhere to return
            </p>
          </div>
        </div>
      )}

      {/* ── Session Expired / App Updated ── */}
      <GlobalDialog
        visible={openLogoutDialog}
        onHide={() => setOpenLogoutDialog(false)}
        closable={false}
        closeOnEscape={false}
        dialogHeader={
          logoutReason === "version" ? "App Updated" : "Session Expired"
        }
      >
        {process.env.REACT_APP_ENVIRONMENT === "live" && (
          <p className="text-center text-gray-600 mb-4">
            Your session has expired. Please log in again.
          </p>
        )}

        {process.env.REACT_APP_ENVIRONMENT !== "live" && (
          <>
            <p className="text-center text-gray-600 mb-2">
              {logoutReason === "version" &&
                "A new version is available. Please log in again."}
              {logoutReason === "session" &&
                "Your access expired. Please log in again."}
            </p>
            <div
              className={`text-center text-xs font-mono mb-3 px-2 py-1 rounded ${
                logoutReason === "version"
                  ? "bg-blue-100 text-blue-600"
                  : "bg-red-100 text-red-600"
              }`}
            >
              🔍 Triggered by: <strong>{logoutReason || "unknown"}</strong>
            </div>
          </>
        )}

        <GlobalButton
          label="Logout"
          isPrimary
          buttonClassName="w-full"
          onClick={logout}
        />
      </GlobalDialog>
    </div>
  );
}

export default App;
